import { AppBarProps, Drawer, Link } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { useMemo } from 'react'

import { getUserDisplayName, useAuthUser } from '@/features/auth'
import { UserRoles } from '@/features/auth/types'
import { useLocation, useNavigate } from '@/hooks'
import routes from '@/routes'
import { useGetTenantByIdQuery, useListTenantsQuery } from '@/services/api/tenant/tenantsEnhanced'
import { RootState, useTypedSelector } from '@/store'
import { CompanyLogo } from '../AppToolBar/Components/CompanyLogo/CompanyLogo'
import { CompanyLogoSmall } from '../AppToolBar/Components/CompanyLogo/CompanyLogoSmall'
import ImageToSvgConverter from '../ImageToSvgConverter/ImageToSvgConverter'
import { drawerHeaderStyles, drawerRootStyles, logoLinkStyles } from './SideBarStyles'
import { NavBar } from './SidebarComponents/NavBar/NavBar'
import {
  customerManagementItems,
  globalAdministrationItems,
  learningManagementItems,
  myAccountItems,
  SidebarItem,
  SidebarItemKey,
  resolveActiveItem,
  resolveInitialOpenSection,
} from './SidebarComponents/NavBar/NavBarHelpers'

type SideBarProps = AppBarProps & {
  isOpen?: boolean
  onDrawerToggle: () => void
  onForceOpen?: () => void
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const userIsAuthorize = (authorize: string[] = [], roles: string[] = []) =>
  roles.some((role) => authorize.includes(role))

// Roles allowed by the backend to read tenant data; participants get 403 on /tenants calls.
const tenantReadRoles = new Set<string>([
  UserRoles.CustomerSuccess,
  UserRoles.GlobalCustomerManager,
  UserRoles.CustomerManager,
])

type AppRoute = {
  path: string
  authorize: string[]
}

const sidebarItems: SidebarItem[] = [
  ...customerManagementItems,
  ...globalAdministrationItems,
  ...learningManagementItems,
  ...myAccountItems,
]
const customerManagementItemKeys = new Set<SidebarItemKey>(customerManagementItems.map((item) => item.key))
const globalAdministrationItemKeys = new Set<SidebarItemKey>(globalAdministrationItems.map((item) => item.key))

export const SideBar = ({ isOpen, onDrawerToggle, onForceOpen }: SideBarProps): JSX.Element => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const tenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const user = useAuthUser()
  const fullName = getUserDisplayName(user)
  const sidebarIsOpen = isOpen ?? true
  const userRoles = useMemo(
    () => (Array.isArray(user?.roles) ? user.roles.map((role) => String(role)) : []),
    [user?.roles],
  )

  const activeItem = resolveActiveItem(location.pathname)
  const initialOpenSection = resolveInitialOpenSection(activeItem)
  const subdomain = new URL(document.URL).hostname.replace('www.', '').split('.')[0]
  const isParentTenant = tenant.is_parent === true

  const isCustomerSuccess = userRoles.includes(UserRoles.CustomerSuccess)
  const canReadTenantData = userRoles.some((role) => tenantReadRoles.has(role))

  const visibleItems = useMemo<SidebarItemKey[]>(() => {
    const shouldHideCustomerManagement = !isParentTenant && !isCustomerSuccess
    const shouldHideGlobalAdministration = !isParentTenant

    return sidebarItems
      .filter((item) => {
        if (shouldHideCustomerManagement && customerManagementItemKeys.has(item.key)) {
          return false
        }

        if (shouldHideGlobalAdministration && globalAdministrationItemKeys.has(item.key)) {
          return false
        }

        // Keep Customer Overview visible for customer_success users even in child tenants.
        // This guarantees tenant switching remains available.
        if (isCustomerSuccess && item.key === 'customerOverview') {
          return true
        }

        if (!item.path) {
          return false
        }

        const route = routes.find((candidate) => candidate.path === item.path) as AppRoute | undefined
        if (!route) {
          return false
        }
        return Boolean(route.authorize?.length) && userIsAuthorize(route.authorize, userRoles)
      })
      .map((item) => item.key)
  }, [isParentTenant, isCustomerSuccess, userRoles])

  // Listing all tenants additionally requires full tenant read access (customer success only).
  const { data: tenantsData } = useListTenantsQuery({ query: '' }, { skip: !isCustomerSuccess })
  const { data: tenantById } = useGetTenantByIdQuery(
    { tenantId: tenant.id },
    {
      skip: !tenant.id || !canReadTenantData,
    },
  )
  const tenantsList = useMemo(
    () => (tenantsData ? (Array.isArray(tenantsData) ? tenantsData : tenantsData.items || []) : []),
    [tenantsData],
  )

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      color="default"
      data-testid="global-admin-drawer"
      open={isOpen}
      onClose={onDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={drawerRootStyles(theme, sidebarIsOpen)}>
      <DrawerHeader sx={drawerHeaderStyles(!!tenant.logo_url)} data-testid="global-admin-drawer-header">
        <Link color="inherit" underline="none" href="/" onClick={navigate} sx={logoLinkStyles(!!tenant.logo_url)}>
          {sidebarIsOpen ? (
            tenant.logo_url ? (
              <ImageToSvgConverter imageString={tenant.logo_url} />
            ) : (
              <CompanyLogo />
            )
          ) : tenant.logo_url ? (
            <ImageToSvgConverter imageString={tenant.logo_url} />
          ) : (
            <CompanyLogoSmall />
          )}
        </Link>
      </DrawerHeader>

      <NavBar
        companyName={tenantById?.name || tenant.name || ''}
        userName={fullName}
        activeItem={activeItem}
        initialOpenSection={initialOpenSection}
        visibleItems={visibleItems}
        tenantSubdomain={subdomain}
        tenantsList={tenantsList}
        onForceOpen={onForceOpen}
        isOpen={sidebarIsOpen}
      />
    </Drawer>
  )
}
